import {
  ElementType,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FavouritesTabIcon from 'components/Icons/FavouritesTabIcon';
import { FetchModelBlocks, ToggleBlockFavourite, getBlocks } from 'redux/ModelsSlice';
import { accent, font, neutral, radius, shadow, text } from 'styles/revampTokens';

export interface BlockNavigationDropdownItem {
  id: string | number;
  title: string;
  source?: any;
  href?: string | null;
}

export interface BlockNavigationClickOptions {
  openInNewTab?: boolean;
}

interface BlockNavigationDropdownProperties {
  blocks: BlockNavigationDropdownItem[];
  onBlockClick: (block: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
  onViewAllBlocksClick: (options?: BlockNavigationClickOptions) => void;
  viewAllHref?: string | null;
  entityLabelPlural?: string;
  getMetaLabel?: (source: any) => string | null;
}

interface CenterSection {
  key: string;
  label: string;
  items: BlockNavigationDropdownItem[];
}

type TabKey = 'all' | 'recent' | 'favourites';

/**
 * Each block gets a soft, tinted avatar. The tint is stable per category (so a
 * category reads as one colour) and falls back to a hash of the block name —
 * deterministic, never a random rainbow, and on the app's muted palette.
 */
const AVATAR_TINTS = [
  { bg: '#F1ECFE', fg: '#7C4EE0' },
  { bg: '#E7F0FF', fg: '#3B74E8' },
  { bg: '#E3F6F0', fg: '#0E9E7E' },
  { bg: '#FFF2E0', fg: '#D9850B' },
  { bg: '#FDEBF1', fg: '#D14D86' },
  { bg: '#ECEEFF', fg: '#5B5FE0' },
] as const;

const RECENT_KEY = '__recent';
const OTHER_KEY = '__other';
const UNCATEGORISED = 'Other';
const RECENT_LIMIT = 4;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'recent', label: 'Recent' },
  { key: 'favourites', label: 'Favourites' },
];

const isModifiedClick = (event: ReactMouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

const isFavouriteBlock = (block: any): boolean =>
  Boolean(block?.favourite || block?.isFavourite || block?.is_favourite || block?.isFavourtie);

const metricsCount = (source: any): number | null =>
  Array.isArray(source?.indicators) ? source.indicators.length : null;

const defaultMetaLabel = (source: any): string | null => {
  const count = metricsCount(source);
  return count === null ? null : `${count} ${count === 1 ? 'metric' : 'metrics'}`;
};

const hashIndex = (value: string, modulo: number): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + (value.codePointAt(index) ?? 0)) % 100_000;
  }
  return hash % modulo;
};

const initialsOf = (title: string): string =>
  title
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase() || '•';

const compactAgo = (value?: string): string | null => {
  if (!value) return null;
  const then = moment.utc(value);
  if (!then.isValid()) return null;
  const now = moment.utc();
  const mins = now.diff(then, 'minutes');
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hours = now.diff(then, 'hours');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(then, 'days');
  if (days < 7) return `${days}d ago`;
  const weeks = now.diff(then, 'weeks');
  if (weeks < 5) return `${weeks}w ago`;
  const months = now.diff(then, 'months');
  if (months < 12) return `${months}mo ago`;
  return `${now.diff(then, 'years')}y ago`;
};

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

const ClockIcon = ({ color = text.muted }: { color?: string }) => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='7' cy='7' r='5.6' stroke={color} strokeWidth='1.1' />
    <path d='M7 4V7L9 8.2' stroke={color} strokeWidth='1.1' strokeLinecap='round' />
  </svg>
);

const GridIcon = ({ color = text.muted }: { color?: string }) => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.9' y='0.9' width='5' height='5' rx='1.2' stroke={color} strokeWidth='1.1' />
    <rect x='8.1' y='0.9' width='5' height='5' rx='1.2' stroke={color} strokeWidth='1.1' />
    <rect x='0.9' y='8.1' width='5' height='5' rx='1.2' stroke={color} strokeWidth='1.1' />
    <rect x='8.1' y='8.1' width='5' height='5' rx='1.2' stroke={color} strokeWidth='1.1' />
  </svg>
);

const FolderIcon = ({ color = text.muted }: { color?: string }) => (
  <svg width='15' height='15' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M1.8 4.2C1.8 3.5 2.3 3 3 3h3l1.3 1.4H13c.7 0 1.2.5 1.2 1.2v6c0 .7-.5 1.2-1.2 1.2H3c-.7 0-1.2-.5-1.2-1.2V4.2Z'
      stroke={color}
      strokeWidth='1.1'
      strokeLinejoin='round'
    />
  </svg>
);

const ArrowIcon = ({ color }: { color: string }) => (
  <svg width='15' height='15' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M11.6667 5.0083L10.4917 6.1833L13.4667 9.16663H3.33334V10.8333H13.4667L10.4917 13.8166L11.6667 14.9916L16.6667 9.99996L11.6667 5.0083Z'
      fill={color}
    />
  </svg>
);

const StarFilledIcon = ({ color }: { color: string }) => (
  <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M8 1.1l1.86 3.77 4.16.6-3.01 2.93.71 4.14L8 11.98l-3.72 1.96.71-4.14L1.98 6.47l4.16-.6L8 1.1Z'
      fill={color}
    />
  </svg>
);

const BlockNavigationDropdown = ({
  blocks,
  onBlockClick,
  getMetaLabel = defaultMetaLabel,
}: BlockNavigationDropdownProperties): ReactElement => {
  const dispatch: any = useDispatch();
  const { modelId } = useParams();
  const modelBlocks = useSelector(getBlocks);

  const [tab, setTab] = useState<TabKey>('all');
  const [query, setQuery] = useState('');
  const [activeRail, setActiveRail] = useState<string>(RECENT_KEY);
  const [activeBlockId, setActiveBlockId] = useState<string | number | null>(null);

  const scrollReference = useRef<HTMLDivElement | null>(null);
  const sectionReferences = useRef<Record<string, HTMLDivElement | null>>({});

  // The authoritative category list (includes empty categories) comes from the
  // model, not from the blocks — otherwise a category with no blocks never
  // appears, which is exactly why "test" was missing.
  const categoryList = useMemo(
    () =>
      (modelBlocks?.categories || [])
        .filter((category: any) => category?.model_category_id != null && category?.category_name)
        .map((category: any) => ({
          id: category.model_category_id as number,
          name: category.category_name as string,
        })),
    [modelBlocks?.categories],
  );

  const categoryNameOf = (block: BlockNavigationDropdownItem): string => {
    const categoryId =
      block.source?.model_category_id != null ? Number(block.source.model_category_id) : null;
    const match =
      categoryId != null
        ? categoryList.find((category: { id: number; name: string }) => category.id === categoryId)
        : undefined;
    return match?.name ?? UNCATEGORISED;
  };

  const tintOf = (block: BlockNavigationDropdownItem) => {
    const name = categoryNameOf(block);
    const seed = name === UNCATEGORISED ? block.title : name;
    return AVATAR_TINTS[hashIndex(seed, AVATAR_TINTS.length)];
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? blocks.filter((block) => block.title.toLowerCase().includes(needle)) : blocks;
  }, [blocks, query]);

  const recent = useMemo(
    () =>
      [...filtered]
        .sort((a, b) =>
          String(b.source?.last_modified_at || '').localeCompare(
            String(a.source?.last_modified_at || ''),
          ),
        )
        .slice(0, RECENT_LIMIT),
    [filtered],
  );

  const favourites = useMemo(
    () => filtered.filter((block) => isFavouriteBlock(block.source)),
    [filtered],
  );

  // Buckets built over the full category list so empty categories still show up.
  const categoryGroups = useMemo(() => {
    const byId = new Map<number, BlockNavigationDropdownItem[]>();
    categoryList.forEach((category: { id: number; name: string }) => byId.set(category.id, []));
    const other: BlockNavigationDropdownItem[] = [];

    filtered.forEach((block) => {
      const categoryId = Number(block.source?.model_category_id);
      if (
        !Number.isNaN(categoryId) &&
        block.source?.model_category_id != null &&
        byId.has(categoryId)
      ) {
        byId.get(categoryId)!.push(block);
      } else {
        other.push(block);
      }
    });

    const groups: CenterSection[] = categoryList.map((category: { id: number; name: string }) => ({
      key: String(category.id),
      label: category.name,
      items: byId.get(category.id) ?? [],
    }));
    if (other.length > 0) {
      groups.push({ key: OTHER_KEY, label: UNCATEGORISED, items: other });
    }
    return groups;
  }, [filtered, categoryList]);

  const searching = query.trim().length > 0;

  const centerSections = useMemo<CenterSection[]>(() => {
    if (tab === 'recent') return [{ key: RECENT_KEY, label: 'Recent', items: recent }];
    if (tab === 'favourites') return [{ key: '__fav', label: 'Favourites', items: favourites }];
    const base: CenterSection[] =
      recent.length > 0 ? [{ key: RECENT_KEY, label: 'Recent', items: recent }] : [];
    // When searching, drop empty category sections; otherwise keep them so the
    // rail and the list agree (and "test" is visible even with no blocks).
    const cats = searching
      ? categoryGroups.filter((group) => group.items.length > 0)
      : categoryGroups;
    return [...base, ...cats];
  }, [tab, recent, favourites, categoryGroups, searching]);

  const railItems = useMemo(
    () =>
      centerSections.map((section) => ({
        key: section.key,
        label: section.label,
        count: section.items.length,
      })),
    [centerSections],
  );

  const flatBlocks = useMemo(
    () => centerSections.flatMap((section) => section.items),
    [centerSections],
  );

  const activeBlock =
    flatBlocks.find((block) => block.id === activeBlockId) ?? flatBlocks[0] ?? null;

  const handleScroll = () => {
    const container = scrollReference.current;
    if (!container || tab !== 'all') return;
    const { top } = container.getBoundingClientRect();
    let current = centerSections[0]?.key ?? RECENT_KEY;
    centerSections.forEach((section) => {
      const element = sectionReferences.current[section.key];
      if (element && element.getBoundingClientRect().top - top <= 14) {
        current = section.key;
      }
    });
    setActiveRail(current);
  };

  const scrollToSection = (key: string) => {
    setActiveRail(key);
    const container = scrollReference.current;
    const element = sectionReferences.current[key];
    if (container && element) {
      const delta = element.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollTo({ top: container.scrollTop + delta - 6, behavior: 'smooth' });
    }
  };

  const goToBlock = (block: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => {
    onBlockClick(block, { openInNewTab: options?.openInNewTab ?? false });
  };

  const toggleFavourite = (block: BlockNavigationDropdownItem) => {
    const blockId = Number(block.source?.id ?? block.id);
    if (!blockId) return;
    dispatch(ToggleBlockFavourite({ blockId, favourite: !isFavouriteBlock(block.source) })).then(
      () => {
        if (modelId) dispatch(FetchModelBlocks({ modelId }));
      },
    );
  };

  const renderRow = (block: BlockNavigationDropdownItem): ReactNode => {
    const tint = tintOf(block);
    const metrics = metricsCount(block.source);
    const subtitle = [categoryNameOf(block), metrics === null ? null : `${metrics} metrics`]
      .filter(Boolean)
      .join(' · ');
    const ago = compactAgo(block.source?.last_modified_at);
    const isActive = activeBlock?.id === block.id;

    return (
      <DropdownFlex
        key={block.id}
        alignItems='center'
        gap='11px'
        px='10px'
        py='7px'
        cursor='pointer'
        borderRadius={radius.control}
        bg={isActive ? neutral.surfaceHover : 'transparent'}
        transition='background 0.1s ease'
        _hover={{ bg: neutral.surfaceHover }}
        onMouseEnter={() => setActiveBlockId(block.id)}
        onClick={(event: ReactMouseEvent) => {
          if (isModifiedClick(event)) return;
          event.stopPropagation();
          goToBlock(block);
        }}
        onContextMenu={(event: ReactMouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          goToBlock(block, { openInNewTab: true });
        }}
      >
        <DropdownFlex
          w='32px'
          h='32px'
          flexShrink={0}
          alignItems='center'
          justifyContent='center'
          borderRadius={radius.control}
          bg={tint.bg}
          color={tint.fg}
          fontFamily={font.body}
          fontSize='12px'
          fontWeight='700'
        >
          {initialsOf(block.title)}
        </DropdownFlex>
        <DropdownFlex direction='column' flex='1 1 0' minW={0} gap='1px'>
          <DropdownBox
            color={text.primary}
            fontFamily={font.body}
            fontSize='13px'
            fontWeight='500'
            lineHeight='17px'
            noOfLines={1}
          >
            {block.title}
          </DropdownBox>
          {subtitle && (
            <DropdownBox color={text.secondary} fontSize='12px' lineHeight='14px' noOfLines={1}>
              {subtitle}
            </DropdownBox>
          )}
        </DropdownFlex>
        {ago && (
          <DropdownBox color={text.muted} fontSize='12px' flexShrink={0} whiteSpace='nowrap'>
            {ago}
          </DropdownBox>
        )}
      </DropdownFlex>
    );
  };

  const metaLabel = activeBlock ? getMetaLabel(activeBlock.source) : null;
  const activeTint = activeBlock ? tintOf(activeBlock) : AVATAR_TINTS[0];
  const activeFavourite = activeBlock ? isFavouriteBlock(activeBlock.source) : false;

  return (
    <DropdownFlex
      w='720px'
      maxW='96vw'
      h='412px'
      maxH='calc(100vh - 96px)'
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      boxShadow={shadow.overlay}
      overflow='hidden'
      borderRadius={radius.overlay}
      flexDirection='column'
      onClick={(event: ReactMouseEvent) => event.stopPropagation()}
    >
      {/* Top bar: search + tabs */}
      <DropdownFlex
        alignItems='center'
        gap='16px'
        px='14px'
        h='46px'
        flexShrink={0}
        borderBottom={`1px solid ${neutral.border}`}
      >
        <DropdownFlex alignItems='center' gap='8px' flex='1 1 0' minW={0}>
          <FigmaSearchIcon width={13} height={13} color={text.muted} />
          <DropdownBox
            as='input'
            type='text'
            value={query}
            placeholder='Search blocks'
            onChange={(event: any) => setQuery(event.target.value)}
            flex='1'
            minW={0}
            bg='transparent'
            border='none'
            outline='none'
            color={text.primary}
            fontFamily={font.body}
            fontSize='13px'
            sx={{ '::placeholder': { color: text.muted } }}
          />
        </DropdownFlex>
        <DropdownFlex gap='4px' flexShrink={0}>
          {TABS.map(({ key, label }) => {
            const selected = key === tab;
            return (
              <DropdownBox
                key={key}
                as='button'
                type='button'
                onClick={() => setTab(key)}
                px='12px'
                h='28px'
                borderRadius='999px'
                fontFamily={font.body}
                fontSize='12px'
                fontWeight='600'
                bg={selected ? accent.subtle : 'transparent'}
                color={selected ? accent.solid : text.secondary}
                transition='background 0.1s ease, color 0.1s ease'
                _hover={selected ? undefined : { bg: neutral.surfaceHover }}
              >
                {label}
              </DropdownBox>
            );
          })}
        </DropdownFlex>
      </DropdownFlex>

      <DropdownFlex flex='1 1 auto' minH={0}>
        {/* Left rail — category navigation (All view only) */}
        {tab === 'all' && (
          <DropdownFlex
            direction='column'
            w='172px'
            flexShrink={0}
            py='8px'
            px='8px'
            gap='2px'
            overflowY='auto'
            borderRight={`1px solid ${neutral.border}`}
          >
            {railItems.map((item) => {
              const selected = item.key === activeRail;
              const color = selected ? accent.solid : text.muted;
              return (
                <DropdownFlex
                  key={item.key}
                  alignItems='center'
                  gap='9px'
                  px='10px'
                  py='7px'
                  cursor='pointer'
                  borderRadius={radius.control}
                  bg={selected ? accent.subtle : 'transparent'}
                  transition='background 0.1s ease'
                  _hover={selected ? undefined : { bg: neutral.surfaceHover }}
                  onClick={() => scrollToSection(item.key)}
                >
                  {item.key === RECENT_KEY ? (
                    <ClockIcon color={color} />
                  ) : (
                    <FolderIcon color={color} />
                  )}
                  <DropdownBox
                    flex='1 1 0'
                    minW={0}
                    color={selected ? accent.solid : text.primary}
                    fontFamily={font.body}
                    fontSize='13px'
                    fontWeight={selected ? '600' : '500'}
                    noOfLines={1}
                  >
                    {item.label}
                  </DropdownBox>
                  <DropdownBox color={text.muted} fontSize='12px' flexShrink={0}>
                    {item.count}
                  </DropdownBox>
                </DropdownFlex>
              );
            })}
          </DropdownFlex>
        )}

        {/* Center — grouped, scrollable block list */}
        <DropdownFlex
          ref={scrollReference}
          onScroll={handleScroll}
          direction='column'
          flex='1 1 0'
          minW={0}
          overflowY='auto'
          p='6px'
        >
          {flatBlocks.length > 0 || (tab === 'all' && !searching) ? (
            centerSections.map((section) => (
              <DropdownFlex
                key={section.key}
                direction='column'
                ref={(element: HTMLDivElement | null) => {
                  sectionReferences.current[section.key] = element;
                }}
              >
                <DropdownBox
                  px='10px'
                  pt='10px'
                  pb='4px'
                  color={text.muted}
                  fontFamily={font.body}
                  fontSize='11px'
                  fontWeight='700'
                  letterSpacing='0.06em'
                  textTransform='uppercase'
                >
                  {section.label}
                </DropdownBox>
                {section.items.length > 0 ? (
                  section.items.map((block) => renderRow(block))
                ) : (
                  <DropdownBox px='10px' pb='8px' color={text.muted} fontSize='12px'>
                    No blocks yet
                  </DropdownBox>
                )}
              </DropdownFlex>
            ))
          ) : (
            <DropdownBox
              px='12px'
              py='28px'
              textAlign='center'
              color={text.secondary}
              fontSize='13px'
            >
              {searching ? 'No blocks match your search' : 'No blocks here yet'}
            </DropdownBox>
          )}
        </DropdownFlex>

        {/* Right — detail / preview of the active block */}
        <DropdownFlex
          direction='column'
          w='232px'
          flexShrink={0}
          p='16px'
          gap='13px'
          borderLeft={`1px solid ${neutral.border}`}
          bg={neutral.surfaceMuted}
        >
          {activeBlock ? (
            <>
              <DropdownFlex direction='column' gap='10px'>
                <DropdownFlex
                  w='44px'
                  h='44px'
                  alignItems='center'
                  justifyContent='center'
                  borderRadius={radius.surface}
                  bg={activeTint.bg}
                  color={activeTint.fg}
                  fontFamily={font.body}
                  fontSize='16px'
                  fontWeight='700'
                >
                  {initialsOf(activeBlock.title)}
                </DropdownFlex>
                <DropdownFlex direction='column' gap='3px'>
                  <DropdownBox
                    color={text.primary}
                    fontFamily={font.display}
                    fontSize='15px'
                    fontWeight='600'
                    lineHeight='20px'
                    noOfLines={2}
                  >
                    {activeBlock.title}
                  </DropdownBox>
                  <DropdownBox color={text.secondary} fontSize='12px' noOfLines={1}>
                    {[categoryNameOf(activeBlock), metaLabel].filter(Boolean).join(' · ')}
                  </DropdownBox>
                </DropdownFlex>
              </DropdownFlex>

              {activeBlock.source?.description && (
                <DropdownBox
                  color={text.secondary}
                  fontFamily={font.body}
                  fontSize='12px'
                  lineHeight='17px'
                  noOfLines={3}
                >
                  {activeBlock.source.description}
                </DropdownBox>
              )}

              <DropdownFlex direction='column' gap='8px'>
                {compactAgo(activeBlock.source?.last_modified_at) && (
                  <DropdownFlex alignItems='center' gap='8px'>
                    <ClockIcon />
                    <DropdownBox color={text.secondary} fontSize='12px'>
                      Updated {compactAgo(activeBlock.source?.last_modified_at)}
                    </DropdownBox>
                  </DropdownFlex>
                )}
                {metaLabel && (
                  <DropdownFlex alignItems='center' gap='8px'>
                    <GridIcon />
                    <DropdownBox color={text.secondary} fontSize='12px'>
                      {metaLabel}
                    </DropdownBox>
                  </DropdownFlex>
                )}
              </DropdownFlex>

              <DropdownFlex direction='column' gap='8px' mt='auto'>
                <DropdownFlex
                  as='button'
                  type='button'
                  alignItems='center'
                  justifyContent='center'
                  gap='6px'
                  h='36px'
                  borderRadius={radius.control}
                  bg={accent.solid}
                  color='#FFFFFF'
                  fontFamily={font.body}
                  fontSize='13px'
                  fontWeight='600'
                  transition='background 0.1s ease'
                  _hover={{ bg: accent.hover }}
                  onClick={() => goToBlock(activeBlock)}
                >
                  Go to block
                  <ArrowIcon color='#FFFFFF' />
                </DropdownFlex>
                <DropdownFlex
                  as='button'
                  type='button'
                  alignItems='center'
                  justifyContent='center'
                  gap='7px'
                  h='36px'
                  borderRadius={radius.control}
                  bg={activeFavourite ? accent.solid : neutral.surface}
                  border={`1px solid ${activeFavourite ? accent.solid : neutral.border}`}
                  color={activeFavourite ? '#FFFFFF' : text.primary}
                  fontFamily={font.body}
                  fontSize='13px'
                  fontWeight='500'
                  transition='background 0.1s ease'
                  _hover={{ bg: activeFavourite ? accent.hover : neutral.surfaceHover }}
                  onClick={() => toggleFavourite(activeBlock)}
                >
                  {activeFavourite ? (
                    <StarFilledIcon color='#FFFFFF' />
                  ) : (
                    <FavouritesTabIcon width={14} height={14} color={text.secondary} />
                  )}
                  {activeFavourite ? 'Remove from favourites' : 'Add to favourites'}
                </DropdownFlex>
              </DropdownFlex>
            </>
          ) : (
            <DropdownBox color={text.secondary} fontSize='13px' m='auto' textAlign='center'>
              Hover a block to preview it
            </DropdownBox>
          )}
        </DropdownFlex>
      </DropdownFlex>
    </DropdownFlex>
  );
};

export default BlockNavigationDropdown;
